import React, {
  Children,
  ComponentProps,
  createContext,
  FC,
  forwardRef,
  Fragment,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { NavLink } from 'react-router-dom';

import { AnimatePresence, AnimatePresenceProps, motion } from 'framer-motion';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { useLocalStorageState } from '@components/component-helpers';
import { IconButton } from '@components/icon-button';
import type { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCollapseNav } from '@components/icons/collapse-nav';
import { IconDropdown } from '@components/icons/dropdown';
import { IconExpandNav } from '@components/icons/expand-nav';
import { IconLock } from '@components/icons/lock';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { Link } from '@components/link';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { SeparatorProps } from '@components/separator/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { classNames } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';
import { StrictUnion } from '@utils/types';

export type NavProps = {
  children: ReactNode;
  stateStorageKey?: string;
  fixedHeader?: JSX.Element;
  fixedFooter?: JSX.Element;
} & StrictUnion<
  | {
      variant?: 'base';
      initialFixed?: boolean;
    }
  | {
      variant?: 'static' | 'cover';
    }
>;

type NavContextValue = {
  collapsable: boolean;
  isFixed: boolean;
  isOpen: boolean;
  toggleFixed(): void;
  unsetMenuOverride(): void;
  setMenuOverride(): void;
};

const NavContext = createContext<NavContextValue>({} as NavContextValue);

export const useNavContext = () => useContext(NavContext);

export const Nav = ({
  children,
  variant = 'base',
  initialFixed = false,
  stateStorageKey = '',
  fixedHeader,
  fixedFooter,
  ...rest
}: NavProps) => {
  const [isFixed, setIsFixed] = useLocalStorageState({
    defaultValue: initialFixed,
    key: stateStorageKey,
  });

  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const toggleFixed = useCallback(() => setIsFixed(!isFixed), [isFixed, setIsFixed]);
  const unsetMenuOverride = useCallback(() => setMenuOpen(false), []);
  const setMenuOverride = useCallback(() => setMenuOpen(true), []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const shouldClose = !navRef.current?.contains(target);

      if (shouldClose) {
        setMenuOpen(false);
        setHovered(false);
      }
    };

    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, []);

  const collapsable = variant === 'base';
  const isOpen = variant === 'cover' ? true : collapsable ? isFixed || menuOpen || hovered : true;
  const desiredWidth = variant === 'cover' ? '100%' : pxToRem(240);

  const variants = {
    open: { width: desiredWidth },
    closed: { width: pxToRem(88) },
  };

  return (
    <NavContext.Provider
      value={{
        collapsable,
        isFixed,
        isOpen,
        setMenuOverride,
        toggleFixed,
        unsetMenuOverride,
      }}
    >
      <motion.div
        id="nav-fixed-container"
        style={{ flexShrink: 0 }}
        variants={variants}
        animate={isFixed || variant === 'static' ? 'open' : 'closed'}
        initial={false}
        transition={{ duration: 0.2 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <NavContainer
          className="dark"
          data-is-open={isOpen}
          ref={navRef}
          $isOpen={isOpen}
          variants={variants}
          animate={isOpen ? 'open' : 'closed'}
          initial={false}
          transition={{ duration: 0.15 }}
          {...rest}
        >
          <div style={{ paddingRight: isOpen ? pxToRem(16) : undefined }}>{fixedHeader}</div>
          <NavBodyContainer className="nav-body-container" $isOpen={isOpen}>
            {children}
          </NavBodyContainer>
          <AnimatePresence initial={false}>
            {isOpen && fixedFooter && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ paddingRight: pxToRem(16) }}
                >
                  {fixedFooter}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </NavContainer>
      </motion.div>
    </NavContext.Provider>
  );
};

export type NavButtonProps = {
  iconLeft: ReactNode;
  iconRight?: ReactNode;
  label: ReactNode;
  onClick(): void;
  heading?: boolean;
  isSelected?: boolean;
};

const NavButton = ({
  iconLeft,
  iconRight,
  label,
  onClick,
  heading,
  isSelected,
}: NavButtonProps) => {
  return (
    <button
      data-selected={isSelected}
      type="button"
      className="nav--item"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      <span className="nav--icon">{iconLeft}</span>
      <HiddenOnOpen>{label}</HiddenOnOpen>
      <ShownOnOpen>
        <AnimatedLabel heading={heading}>{label}</AnimatedLabel>
        {iconRight && <AnimatedIcon>{iconRight}</AnimatedIcon>}
      </ShownOnOpen>
    </button>
  );
};

export type NavMenuProps = {
  children: ReactNode;
  icon: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  label: string;
};

const menuMotionProps = {
  initial: { opacity: 0, x: -16 },
  animate: { opacity: 1, x: 0 },
  transition: { type: 'spring', damping: 50, stiffness: 1000 },
};

const NavMenu = ({ icon, iconRight, label, children, ...rest }: NavMenuProps) => {
  return (
    <Menu
      triggerProps={{ iconLeft: icon, iconRight, label }}
      trigger={NavMenuTrigger}
      motionProps={menuMotionProps}
      placement="right-end"
      {...rest}
    >
      {children}
    </Menu>
  );
};

const NavSpacer = () => <div className="nav--spacer" />;

const NavSeparator = ({ className = 'nav--separator', margin, ...rest }: SeparatorProps) => (
  <Separator className={className} color={theme.color.whiteTrans10} {...rest} />
);

const NavGroup = ({ children }: { children: ReactNode }) => {
  return (
    <ul className="nav--group">
      {Children.toArray(children).map((child, i) => (
        <li key={i}>{child}</li>
      ))}
    </ul>
  );
};

const NavGroupHeader = ({ text }: { text: string }) => {
  return <Text className="nav--group-header" text={text} font={'bold14'} color="purple20" />;
};

export type NavMenuTriggerProps = HTMLAttributes<HTMLButtonElement> & {
  iconLeft: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  label: string;
};

const NavMenuTrigger = forwardRef<HTMLButtonElement, NavMenuTriggerProps>((props, ref) => {
  const { label, iconLeft, iconRight, onClick, ...rest } = props;
  const { isOpen, setMenuOverride } = useNavContext();

  return (
    <button
      {...rest}
      className="nav--item"
      type="button"
      ref={ref}
      onClick={(e) => {
        onClick?.(e);
        setMenuOverride();
      }}
    >
      <span className="nav--icon">{iconLeft}</span>
      {!isOpen && <VisuallyHidden>{label}</VisuallyHidden>}
      <AnimatePresence>
        {isOpen && (
          <Fragment>
            <AnimatedLabel>{label}</AnimatedLabel>
            {iconRight && <AnimatedIcon>{iconRight}</AnimatedIcon>}
          </Fragment>
        )}
      </AnimatePresence>
    </button>
  );
});

export type NavItemLinkProps = {
  href: string;
  iconLeft: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  label: string | ReactElement;
  isDisabled?: boolean;
  onClick?: () => void;
  isActive?: boolean;
};

const NavItemLink = ({
  href,
  iconRight,
  iconLeft,
  label,
  isDisabled,
  onClick,
  isActive,
  ...rest
}: NavItemLinkProps) => {
  const hasCustomActiveLogic = isActive !== undefined;

  return isDisabled ? (
    <Stack space={2} orientation="horizontal" align="center" style={{ padding: pxToRem(8) }}>
      <iconLeft.type {...iconLeft.props} color="purple50" />
      <HiddenOnOpen>{label}</HiddenOnOpen>
      <ShownOnOpen>
        <Text font="regular14" color="purple50" text={label} />
      </ShownOnOpen>
      {iconRight && <AnimatedIcon>{iconRight}</AnimatedIcon>}
    </Stack>
  ) : (
    <NavLink
      to={href}
      {...rest}
      className={({ isActive: routerIsActive }) => {
        const active = hasCustomActiveLogic ? isActive : routerIsActive;
        return classNames('nav--item', { active });
      }}
      data-custom-active={hasCustomActiveLogic ? (isActive ? 'true' : 'false') : undefined}
      aria-current={hasCustomActiveLogic ? (isActive ? 'page' : false) : undefined}
      onClick={onClick}
    >
      <span className="nav--icon">{iconLeft}</span>
      <HiddenOnOpen>{label}</HiddenOnOpen>
      <ShownOnOpen initial={false}>
        <AnimatedLabel>{label}</AnimatedLabel>
        {iconRight && <AnimatedIcon>{iconRight}</AnimatedIcon>}
      </ShownOnOpen>
    </NavLink>
  );
};
const NavGroupItemLink = ({
  href,
  iconRight,
  iconLeft,
  label,
  isDisabled,
  onClick,
}: NavItemLinkProps) => {
  return isDisabled ? (
    <Stack space={1} orientation="horizontal" align="center" style={{ padding: pxToRem(8) }}>
      <iconLeft.type {...iconLeft.props} color="purple50" />
      <Text font="regular14" color="purple50" text={label} />
      {iconRight && <AnimatedIcon>{iconRight}</AnimatedIcon>}
    </Stack>
  ) : (
    <NavLink to={href} className="nav--group-item" onClick={onClick}>
      <span className="nav--icon">{iconLeft}</span>
      <AnimatePresence initial={false}>
        <Fragment>
          <AnimatedLabel>{label}</AnimatedLabel>
          {iconRight && <AnimatedIcon>{iconRight}</AnimatedIcon>}
        </Fragment>
      </AnimatePresence>
    </NavLink>
  );
};

type NavGroupItemHeaderProps = {
  iconLeft: ReactElement<IconProps>;
  label: string | ReactElement;
  id: string;
  toggleGroup: (id: string) => void;
  isCollapsed: (id: string) => boolean;
  addSidebar?: () => void;
};

const NavGroupItemHeader = ({
  iconLeft,
  label,
  id,
  toggleGroup,
  isCollapsed,
  addSidebar,
}: NavGroupItemHeaderProps) => {
  const { isOpen } = useNavContext();
  return (
    <Stack orientation="horizontal" justify="space-between" align="center">
      <GroupContainer>
        <Stack space={0} orientation="horizontal" align="center" onClick={() => toggleGroup(id)}>
          {isOpen ? (
            <>
              <Text text={label} font="semibold14" color="white100" />
              <IconContainer className={!isCollapsed(id) ? 'arrow down' : 'arrow right'}>
                <IconDropdown color="purple50" />
              </IconContainer>
            </>
          ) : (
            <span className="nav--icon">{iconLeft}</span>
          )}
        </Stack>
      </GroupContainer>
      {addSidebar && (
        <ShownOnOpen>
          <NavbarButtonStyledContainer>
            <IconButton
              dataTestid="add-button"
              size="sm"
              variant="ghost-purple"
              icon={<IconAddPlus />}
              onClick={addSidebar}
            />
          </NavbarButtonStyledContainer>
        </ShownOnOpen>
      )}
    </Stack>
  );
};

export type NavExpandableSectionProps = {
  icon: ReactElement<IconProps>;
  label: string | ReactElement;
  groupId: string;
  collapseId: string;
  toggleGroup: (id: string) => void;
  isCollapsed: (id: string) => boolean;
  isActive: boolean;
  isPrivate: boolean;
  options: {
    label: string;
    icon: ReactElement<IconProps>;
    isDanger?: boolean;
    onClick: () => void;
  }[];
  links: NavItemLinkProps[];
};

const NavExpandableSectionHeader = ({
  icon,
  label,
  collapseId,
  groupId,
  toggleGroup,
  isCollapsed: getIsCollapsed = noop as any,
  isActive,
  isPrivate,
  options = [],
  links = [],
  ...rest
}: NavExpandableSectionProps) => {
  const isCollapsed = getIsCollapsed(`${collapseId}-${groupId}`);
  return (
    <Fragment key={groupId}>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <GroupContainer
          $fullWidth
          $isCollapsable={true}
          onClick={() => toggleGroup(`${collapseId}-${groupId}`)}
          data-active={isActive && isCollapsed}
        >
          <Stack
            space={2}
            orientation="horizontal"
            align="center"
            style={{ overflow: 'hidden' }}
            {...rest}
          >
            {icon}
            <ShownOnOpen>
              <Stack
                orientation="horizontal"
                justify="space-between"
                style={{ overflow: 'hidden' }}
              >
                <Stack orientation="horizontal" align="center" style={{ overflow: 'hidden' }}>
                  <Stack orientation="horizontal" space={1} align="center" style={{ minWidth: 0 }}>
                    <Text text={label} font="regular14" color="white100" ellipsisWithTooltip />
                    {isPrivate && <IconLock size={16} color="purple40" />}
                  </Stack>

                  <IconContainer className={isCollapsed ? 'arrow right' : 'arrow down'}>
                    <IconDropdown color="purple50" />
                  </IconContainer>
                </Stack>
              </Stack>
            </ShownOnOpen>
          </Stack>
          {options.length > 0 && (
            <ShownOnOpen>
              <Menu
                data-options-visible="true"
                buttonProps={{
                  variant: 'ghost',
                  size: 'sm',
                  iconLeft: <IconMoreVertical size={16} />,
                }}
              >
                {options.map((option) => (
                  <Menu.Item
                    key={option.label}
                    variant={option.isDanger ? 'danger' : 'base'}
                    onSelect={option.onClick}
                    label={option.label}
                    icon={option.icon}
                  />
                ))}
              </Menu>
            </ShownOnOpen>
          )}
        </GroupContainer>
      </Stack>
      <ShownOnOpen>
        <Collapse isCollapsed={isCollapsed}>
          <Stack space={0} orientation="horizontal">
            <GroupLine />
            <Nav.Group>
              {links.map((link, index) => (
                <Nav.GroupLink key={`${groupId}-link-${index}`} {...link} />
              ))}
            </Nav.Group>
          </Stack>
        </Collapse>
      </ShownOnOpen>
    </Fragment>
  );
};

const GroupContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'center',
})<{ $fullWidth?: boolean; $isCollapsable?: boolean }>(({ $fullWidth, $isCollapsable }) => ({
  cursor: 'pointer',
  height: pxToRem(32),
  width: $fullWidth ? '100%' : 'auto',
  borderRadius: pxToRem(4),
  padding: pxToRem($isCollapsable ? 8 : 12),
  paddingRight: pxToRem(0),
  '[data-options-visible="true"]': {
    display: 'none',
  },
  '&:hover, &[data-active="true"]': {
    background: theme.color.whiteTrans5,
  },
  '&:hover, &:has(button[data-active="true"])': {
    '[data-options-visible="true"]': {
      display: 'block',
    },
  },
}));

const IconContainer = styled.span({
  display: 'flex',
  transition: transitionStandard('transform'),
  '&.right': {
    transform: 'rotate(-90deg)',
  },
});

export type NavGroupType = {
  id: string;
  label: string;
  icon: ReactElement<IconProps>;
  isActive: boolean;
  isPrivate: boolean;
  options: Array<{
    onClick: () => void;
    icon: ReactElement<IconProps>;
    label: string;
    isDanger?: boolean;
  }>;
  links: NavItemLinkProps[];
};

export const NavSectionPlaceholder = ({
  group,
  isCollapsed,
}: {
  group: NavGroupType;
  isCollapsed: boolean;
}) => {
  return (
    <NavSectionPlaceholderContainer>
      <Stack
        space={2}
        orientation="horizontal"
        align="center"
        style={{ backgroundColor: theme.color.purple400, padding: pxToRem(4) }}
      >
        {group.icon}
        <Text text={group.label} ellipsis color="white100" />
      </Stack>
      {!isCollapsed && (
        <Nav.Group>
          {group.links.map((link) => (
            <Nav.Link key={link.href} {...link} />
          ))}
        </Nav.Group>
      )}
    </NavSectionPlaceholderContainer>
  );
};

const NavSectionPlaceholderContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  '.nav--group': {
    backgroundColor: theme.color.purple400,
    color: theme.color.white100,
    listStyleType: 'none !important',
    li: {
      margin: `${pxToRem(16)} 0`,
      a: {
        display: 'flex',
        alignItems: 'center',
        '.nav--icon': {
          color: theme.color.purple40,
          width: pxToRem(16),
          height: pxToRem(16),
          marginRight: pxToRem(8),
        },
      },
    },
  },
});

export const NavLinkPlaceholderContainer = styled.div({
  backgroundColor: theme.color.purple400,
  color: theme.color.whiteTrans80,
  a: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '.nav--icon': {
      height: pxToRem(16),
      marginRight: pxToRem(8),
    },
    '.nav--label': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  },
});

const AnimatedLabel = ({
  children,
  heading = false,
}: {
  children: ReactNode;
  heading?: boolean;
}) => (
  <motion.span
    exit={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    initial={{ opacity: 0 }}
    className={`nav--label ${heading ? 'nav--label-heading' : ''}`}
  >
    {children}
  </motion.span>
);

type NavHeaderProps = {
  logo: (({ isOpen }: { isOpen: boolean }) => ReactNode) | ReactNode;
};

const NavHeader: FC<NavHeaderProps> = ({ logo, children }) => {
  const { t } = useScopedTranslate(locale);
  const { isOpen, isFixed, collapsable, toggleFixed } = useNavContext();
  return (
    <Fragment>
      <Stack
        space={8}
        orientation="horizontal"
        justify="space-between"
        align="center"
        className="nav--header"
      >
        {typeof logo === 'function' ? logo({ isOpen }) : logo}
        <AnimatePresence>
          {collapsable && isOpen && (
            <AnimatedFixButton>
              <NavbarButtonStyledContainer>
                <IconButton
                  variant="ghost-purple"
                  icon={isFixed ? <IconCollapseNav /> : <IconExpandNav />}
                  tooltip={isFixed ? t('collapse') : t('expand')}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFixed();
                  }}
                />
              </NavbarButtonStyledContainer>
            </AnimatedFixButton>
          )}
        </AnimatePresence>
      </Stack>
      {children && <NavSeparator className="nav--header-separator" />}
      {children}
      <NavSeparator className="nav--header-separator" />
    </Fragment>
  );
};

const AnimatedFixButton = ({ children }: { children: ReactElement }) => (
  <motion.div
    animate={{ opacity: 1, transition: { delay: 0.2 } }}
    initial={{ opacity: 0 }}
    exit={{ opacity: 0, transition: { duration: 0.1 } }}
  >
    {children}
  </motion.div>
);

const AnimatedIcon = ({ children }: { children: ReactNode }) => (
  <motion.span
    animate={{ opacity: 1, transition: { delay: 0.2 } }}
    initial={{ opacity: 0 }}
    exit={{ opacity: 0, transition: { duration: 0.1 } }}
    className="nav--icon-right"
  >
    {children}
  </motion.span>
);

const NavItemLinkExternal = ({
  href,
  iconLeft,
  iconRight = <IconOpenNewTab size={16} />,
  label,
  onClick,
}: NavItemLinkProps) => {
  return (
    <ExternalLink href={href} onClick={onClick}>
      <span className="nav--icon">{iconLeft}</span>
      <HiddenOnOpen>{label}</HiddenOnOpen>
      <ShownOnOpen>
        <Fragment>
          <AnimatedLabel>{label}</AnimatedLabel>
          <AnimatedIcon>{iconRight}</AnimatedIcon>
        </Fragment>
      </ShownOnOpen>
    </ExternalLink>
  );
};

const ExternalLink = styled.a.attrs({
  target: '_blank',
  className: 'nav--item',
})({
  '&&&>.nav--icon-right': {
    height: pxToRem(16),
    width: pxToRem(16),
  },
});

export const ShownOnOpen: React.FC<AnimatePresenceProps> = ({ children, ...rest }) => {
  const { isOpen } = useNavContext();
  return <AnimatePresence {...rest}>{isOpen ? <>{children}</> : null}</AnimatePresence>;
};

const HiddenOnOpen: React.FC = ({ children }) => {
  const { isOpen } = useNavContext();
  return isOpen ? <VisuallyHidden>{children}</VisuallyHidden> : null;
};

const WrapOnClosed: React.FC<{ wrapper: React.ComponentType }> = ({ wrapper, children }) => {
  const { isOpen } = useNavContext();
  const Wrapper = isOpen ? Fragment : wrapper;
  return <Wrapper>{children}</Wrapper>;
};

const NavMenuItem = (props: ComponentProps<typeof Menu.Item>) => {
  const { unsetMenuOverride } = useNavContext();
  const { onSelect, ...rest } = props;
  return (
    <Menu.Item
      selectionType="through"
      {...rest}
      onSelect={(event) => {
        onSelect?.(event);
        unsetMenuOverride();
      }}
    />
  );
};

const NavMenuItemLink = (props: ComponentProps<typeof Menu.ItemLink>) => {
  const { unsetMenuOverride } = useNavContext();
  const { onSelect, to, isExternal, ...rest } = props;
  return (
    <Link to={to} target={isExternal ? '_blank' : '_self'}>
      <Menu.Item
        selectionType="through"
        {...rest}
        onSelect={(event) => {
          onSelect?.(event);
          unsetMenuOverride();
        }}
      />
    </Link>
  );
};

Nav.Button = NavButton;
Nav.ExternalLink = NavItemLinkExternal;
Nav.Group = NavGroup;
Nav.GroupHeader = NavGroupHeader;
Nav.GroupItemHeader = NavGroupItemHeader;
Nav.ExpandableSectionHeader = NavExpandableSectionHeader;
Nav.SectionPlaceholder = NavSectionPlaceholder;
Nav.Header = NavHeader;
Nav.Link = NavItemLink;
Nav.GroupLink = NavGroupItemLink;
Nav.Menu = NavMenu;
Nav.MenuItem = NavMenuItem;
Nav.MenuItemLink = NavMenuItemLink;
Nav.Separator = NavSeparator;
Nav.Spacer = NavSpacer;
Nav.ShownOnOpen = ShownOnOpen;
Nav.WrapOnClosed = WrapOnClosed;

const GroupLine = styled.div({
  borderRightWidth: pxToRem(1),
  borderRightColor: theme.color.whiteTrans10,
  borderRightStyle: 'solid',
  margin: `${pxToRem(8)} ${pxToRem(8 - 1)} ${pxToRem(8)} ${pxToRem(16)} `,
});

type IsOpenProp = { $isOpen: boolean };

const NavBodyContainer = styled.div<IsOpenProp>(({ $isOpen }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
  paddingRight: $isOpen ? pxToRem(16) : undefined,
  div: {
    overflowX: 'clip',
  },
}));

const NavContainer = styled(motion.nav)<IsOpenProp>(({ $isOpen }) => ({
  position: 'absolute',
  zIndex: 100,
  top: 0,
  left: 0,
  bottom: 0,
  backgroundColor: theme.color.purple400,
  padding: $isOpen
    ? `${pxToRem(0)} ${pxToRem(0)} ${pxToRem(24)} ${pxToRem(24)}`
    : `${pxToRem(0)} ${pxToRem(24)}`,
  marginRight: pxToRem(16),

  boxShadow: `inset ${pxToRem(-1)} 0 0 ${theme.color.uiTrans10}`,
  display: 'flex',
  flexDirection: 'column',
  transition: transitionStandard('background-color'),

  '.nav--header': {
    height: pxToRem(32),
    margin: `${pxToRem(8)} ${pxToRem(8)}`,
    marginTop: pxToRem(24),
    font: theme.font.bold14,
    color: theme.color.white100,
  },

  '.nav--header-separator, .nav--separator': {
    margin: `${pxToRem(8)} ${pxToRem(0)}`,
  },

  '.nav--spacer': {
    minHeight: 0,
    flexGrow: 1,
    flexShrink: 1,
  },

  '.nav--group': {
    width: '100%',
    margin: 0,
    padding: 0,
    listStyleType: 'none',
    textIndent: 0,
  },

  '.nav--group-header': {
    padding: pxToRem(8),
  },

  '.nav--item': {
    width: '100%',
    position: 'relative',
    display: 'flex',
    background: 'transparent',
    alignItems: 'center',
    height: pxToRem(32),
    font: theme.font.regular14,
    borderRadius: pxToRem(4),
    transition: transitionStandard('all'),
    outline: 'none',
    cursor: 'pointer',
    padding: pxToRem(8),
    paddingLeft: pxToRem(12),

    '.nav--icon': {
      color: theme.color.purple50,
      marginRight: pxToRem(8),
      width: pxToRem(16),
      height: pxToRem(16),
      transition: transitionStandard('color'),
    },

    '.nav--icon-right': {
      color: theme.color.purple50,
      width: pxToRem(24),
      height: pxToRem(24),
      transition: transitionStandard('color'),
    },

    '.nav--label': {
      color: theme.color.white100,
      flexGrow: 1,
      textAlign: 'left',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      transition: transitionStandard('color'),
    },

    '.nav--label-heading': {
      font: theme.font.bold14,
      textTransform: 'uppercase',
    },

    '&:hover, &:focus-visible, &[data-active="true"], &[aria-current="page"], &.active': {
      background: theme.color.whiteTrans5,
    },

    '&[aria-current="page"]:not([data-custom-active])': {
      background: theme.color.whiteTrans5,

      '.nav--icon': {
        color: theme.color.blue80,
      },

      '.nav--label': {
        color: theme.color.blue80,
      },
    },

    '&[data-selected="true"], &.active': {
      '.nav--icon': {
        color: theme.color.blue80,
      },

      '.nav--label': {
        color: theme.color.blue80,
      },
    },

    '&[data-active="true"]': {
      cursor: 'initial',
    },
  },
  '.nav--group-item': {
    width: '100%',
    position: 'relative',
    display: 'flex',
    background: 'transparent',
    alignItems: 'center',
    height: pxToRem(32),
    font: theme.font.regular14,
    borderRadius: pxToRem(4),
    transition: transitionStandard('all'),
    outline: 'none',
    cursor: 'pointer',
    padding: pxToRem(8),
    paddingLeft: pxToRem(12),

    '.nav--icon': {
      color: theme.color.purple50,
      marginRight: pxToRem(8),
      width: pxToRem(16),
      height: pxToRem(16),
      transition: transitionStandard('color'),
    },
    '.nav--icon-right': {
      color: theme.color.purple50,
      width: pxToRem(24),
      height: pxToRem(24),
      transition: transitionStandard('color'),
    },

    '.nav--label': {
      color: theme.color.white100,
      flexGrow: 1,
      textAlign: 'left',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      transition: transitionStandard('color'),
    },

    '.nav--label-heading': {
      font: theme.font.bold14,
      textTransform: 'uppercase',
    },

    '&:hover, &:focus-visible, &[data-active="true"], &[aria-current="page"]': {
      background: theme.color.whiteTrans5,
    },

    '&[aria-current="page"], &[data-selected="true"]': {
      '.nav--icon': {
        color: theme.color.blue80,
      },

      '.nav--label': {
        color: theme.color.blue80,
      },
    },

    '&[data-active="true"]': {
      cursor: 'initial',
    },
  },
  '.nav--icon, .nav--icon-right': {
    height: `${pxToRem(16)} !important`,
    width: `${pxToRem(16)} !important`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const hireNavStyle = {
  '&[data-is-open="true"]': {
    '.nav-body-container': {
      paddingRight: pxToRem(24),
    },
    '.unread-notifications': {
      left: pxToRem(4),
    },
  },
  '&[data-is-open="false"]': {
    '.nav--group:last-child': {
      marginBottom: pxToRem(20),
    },
  },

  '.nav--group ': {
    marginTop: pxToRem(4),
    marginBottom: pxToRem(4),
  },

  '.nav--group-item': {
    alignItems: 'center',
  },
};

export const HireNavStyleContainer = styled.div(hireNavStyle);

export const HireNav = styled(Nav)(hireNavStyle);

const locale = {
  en: {
    expand: 'Expand navbar',
    collapse: 'Collapse navbar',
  },
  is: {
    expand: 'Opna valmynd',
    collapse: 'Loka valmynd',
  },
};

export const NavbarButtonStyledContainer = styled.div({
  marginRight: pxToRem(4),
  button: {
    backgroundColor: theme.color.purple400,
    '&:hover': {
      backgroundColor: `${theme.color.purple200} !important`,
      svg: {
        color: theme.color.purple40,
      },
    },
    '&[data-variant="ghost"]': {
      backgroundColor: theme.color.purple400,
    },
  },
});
